import { useMutation } from 'react-query';
import {
  ApiOptimizationDataPoint,
  IntegrationService,
  OptimizationConstraint,
} from './queries';
import request from './request';

export type ContactFormData = {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone?: string;
  message: string;
  interest: string;
};

export type CreateOptimizationRequest = {
  customer: string;
  optimizationName: string;
  modelBuild: string;
  modelId: string;
  targetInvestment: number;
  period: {
    from: string;
    to: string;
  };
  constraints: Array<OptimizationConstraint>;
};

type CreateOptimizationResponse = { data: ApiOptimizationDataPoint };

export type DeleteOptimizationRequest = {
  customer: string;
  optimizationId: string;
};

// Backend returns empty object but mutation returns optimizationId
export type DeleteOptimizationResponse = {
  optimizationId: string;
};

export function useCreateOptimizationMutation() {
  return useMutation((data: CreateOptimizationRequest) => {
    const { customer, ...bodyData } = data;

    return request
      .post<CreateOptimizationResponse>('/data/optimizations', bodyData, {
        params: {
          customer,
        },
      })
      .then((response) => {
        return response.data.data;
      });
  });
}

export function useDeleteOptimizationMutation() {
  return useMutation(
    ({ customer, optimizationId }: DeleteOptimizationRequest) => {
      return request
        .delete<DeleteOptimizationResponse>('/data/optimizations', {
          params: {
            customer,
            optimization_id: optimizationId,
          },
        })
        .then(() => {
          return { optimizationId };
        });
    },
  );
}

export function useUpdateCustomerMutation() {
  return useMutation((data: { id: string; isRegistrationStarted: boolean }) => {
    return request.post(
      '/data/customers/registration-status',
      {
        isRegistrationStarted: data.isRegistrationStarted,
      },
      {
        params: {
          customer: data.id,
        },
      },
    );
  });
}

export function useContactFormMutation(options?: {
  onSuccess?: () => void;
  onError?: () => void;
}) {
  return useMutation((data: ContactFormData) => {
    return request
      .post('/public/contact/send-email', {
        formData: data,
      })
      .then((response) => {
        return response.data;
      });
  }, options);
}

export type DisconnectDataConnectorRequest = {
  customer: string;
  service: IntegrationService;
};

export function useDisconnectDataConnectorMutation() {
  return useMutation(
    ({ customer, service }: DisconnectDataConnectorRequest) => {
      return request
        .delete('/data/data-connectors', {
          params: {
            customer,
            service,
          },
        })
        .then((response) => {
          return response.data;
        });
    },
  );
}

export type UpdateSelectedAccountsRequest = {
  customer: string;
  service: IntegrationService;
  selectedAccountIds: string[];
};

export function useUpdateSelectedAccountsMutation() {
  return useMutation(
    ({
      customer,
      service,
      selectedAccountIds,
    }: UpdateSelectedAccountsRequest) => {
      return request
        .put(
          '/data/data-connectors/accounts',
          { service, selectedAccountIds },
          {
            params: {
              customer,
            },
          },
        )
        .then((response) => {
          return response.data;
        });
    },
  );
}

export type UpdateCampaignFilterRequest = {
  customer: string;
  service: IntegrationService;
  campaignNameFilter: string | null;
};

export function useUpdateCampaignFilterMutation() {
  return useMutation(
    ({
      customer,
      service,
      campaignNameFilter,
    }: UpdateCampaignFilterRequest) => {
      return request
        .put(
          '/data/data-connectors/campaign-filter',
          { service, campaignNameFilter },
          {
            params: {
              customer,
            },
          },
        )
        .then((response) => {
          return response.data;
        });
    },
  );
}

export function useSendInviteMutation() {
  return useMutation(
    (data: {
      email: string;
      role: string;
      customer?: string;
      companyName?: string;
    }) => {
      return request.post(
        '/data/signup/send-invite-email',
        {
          email: data.email,
          role: data.role,
          companyName: data.companyName,
        },
        {
          params: {
            customer: data.customer,
          },
        },
      );
    },
  );
}
